import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRL = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function EquipePanel() {
  const [workers, setWorkers] = useState([]);
  const [form, setForm] = useState(null);
  const [folhaMonth, setFolhaMonth] = useState(new Date().toISOString().slice(0, 7));
  const [folha, setFolha] = useState(null);
  const [lancados, setLancados] = useState([]);
  const [salvando, setSalvando] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/financeiro/equipe`).then((r) => setWorkers(r.data.workers || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const salvarWorker = async () => {
    const payload = {
      nome: form.nome || "", salario: parseFloat(form.salario) || 0,
      encargos_pct: parseFloat(form.encargos_pct) || 0,
      gratificacao_padrao: parseFloat(form.gratificacao_padrao) || 0,
    };
    try {
      if (form.id) await axios.patch(`${API}/financeiro/equipe/${form.id}`, payload);
      else await axios.post(`${API}/financeiro/equipe`, payload);
      toast.success(form.id ? "Ajustes salvos ✅" : "Trabalhador cadastrado 👷");
      setForm(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao salvar"); }
  };

  const excluir = async (w) => {
    if (!window.confirm(`Remover ${w.nome} da equipe? (lançamentos antigos não mudam)`)) return;
    try { await axios.delete(`${API}/financeiro/equipe/${w.id}`); load(); }
    catch { toast.error("Erro ao remover"); }
  };

  const abrirFolha = async () => {
    try {
      const r = await axios.get(`${API}/financeiro/equipe/folha-status`, { params: { month: folhaMonth } });
      setLancados(r.data.lancados || []);
      setFolha(workers.map((w) => ({
        worker_id: w.id, nome: w.nome,
        salario: w.salario || 0,
        encargos_pct: w.encargos_pct || 0,
        encargos: Math.round((w.salario || 0) * (w.encargos_pct || 0)) / 100,
        gratificacao: w.gratificacao_padrao || 0,
      })));
    } catch { toast.error("Erro ao abrir a folha"); }
  };

  const setFolhaItem = (i, campo, valor) => {
    setFolha((f) => {
      const n = [...f];
      n[i] = { ...n[i], [campo]: valor };
      if (campo === "salario") {
        n[i].encargos = Math.round((parseFloat(valor) || 0) * (n[i].encargos_pct || 0)) / 100;
      }
      return n;
    });
  };

  const confirmarFolha = async () => {
    const itens = folha
      .filter((it) => !lancados.includes(it.worker_id))
      .map((it) => ({ ...it, salario: parseFloat(it.salario) || 0, encargos: parseFloat(it.encargos) || 0, gratificacao: parseFloat(it.gratificacao) || 0 }))
      .filter((it) => it.salario + it.encargos + it.gratificacao > 0);
    if (!itens.length) { toast.error("Nenhum valor a lançar"); return; }
    setSalvando(true);
    try {
      const r = await axios.post(`${API}/financeiro/equipe/folha`, { month: folhaMonth, itens });
      toast.success(`Folha lançada: ${r.data.lancamentos} lançamento(s) 💵`);
      setFolha(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao lançar folha"); }
    finally { setSalvando(false); }
  };

  const totalFolha = (it) => (parseFloat(it.salario) || 0) + (parseFloat(it.encargos) || 0) + (parseFloat(it.gratificacao) || 0);

  return (
    <div className="space-y-3" data-testid="equipe-panel">
      <Card>
        <CardContent className="p-4 space-y-3">
          <div>
            <h3 className="font-bold text-green-900 text-sm">👷 Equipe (CLT + gratificação)</h3>
            <p className="text-[11px] text-gray-500">Cadastre uma vez; todo mês confira, ajuste e lance a folha de uma vez.</p>
          </div>

          {workers.length === 0 && <p className="text-sm text-gray-400 text-center py-2">Nenhum trabalhador cadastrado.</p>}

          <div className="space-y-2" data-testid="equipe-lista">
            {workers.map((w) => (
              <div key={w.id} className="border rounded-lg p-3 flex items-center justify-between gap-2" data-testid={`equipe-worker-${w.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{w.nome}</p>
                  <p className="text-[11px] text-gray-500">{BRL(w.salario)}/mês · encargos {w.encargos_pct}%{w.gratificacao_padrao > 0 ? ` · gratif. ${BRL(w.gratificacao_padrao)}` : ""}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => setForm({ ...w })} className="text-xs px-2.5 py-1.5 rounded-full bg-gray-100 text-gray-700" data-testid={`equipe-editar-${w.id}`}>✏️ Ajustar</button>
                  <button onClick={() => excluir(w)} className="text-xs px-2 py-1.5 rounded-full bg-gray-100 text-red-500" data-testid={`equipe-del-${w.id}`}>🗑</button>
                </div>
              </div>
            ))}
          </div>

          {form ? (
            <div className="border rounded-xl p-3 bg-gray-50 space-y-2" data-testid="equipe-form">
              <Input placeholder="Nome do trabalhador" value={form.nome || ""} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="equipe-form-nome" />
              <div className="grid grid-cols-3 gap-2">
                <div><label className="text-[10px] text-gray-500">Salário (R$)</label><Input type="number" step="any" value={form.salario ?? ""} onChange={(e) => setForm({ ...form, salario: e.target.value })} data-testid="equipe-form-salario" /></div>
                <div><label className="text-[10px] text-gray-500">Encargos (%)</label><Input type="number" step="any" value={form.encargos_pct ?? ""} onChange={(e) => setForm({ ...form, encargos_pct: e.target.value })} data-testid="equipe-form-encargos" /></div>
                <div><label className="text-[10px] text-gray-500">Gratif. padrão</label><Input type="number" step="any" value={form.gratificacao_padrao ?? ""} onChange={(e) => setForm({ ...form, gratificacao_padrao: e.target.value })} data-testid="equipe-form-gratificacao" /></div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setForm(null)} className="flex-1">Cancelar</Button>
                <Button onClick={salvarWorker} disabled={!form.nome} className="flex-1 bg-green-700 hover:bg-green-800" data-testid="equipe-form-salvar">{form.id ? "Salvar ajustes" : "Cadastrar"}</Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setForm({})} className="w-full border-dashed" data-testid="equipe-novo-btn">➕ Cadastrar trabalhador</Button>
          )}
        </CardContent>
      </Card>

      {workers.length > 0 && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <h3 className="font-bold text-green-900 text-sm">💵 Lançar folha do mês</h3>
            {!folha ? (
              <div className="flex gap-2">
                <input type="month" value={folhaMonth} onChange={(e) => setFolhaMonth(e.target.value)} className="border rounded-lg px-3 py-2 text-sm flex-1" data-testid="folha-mes" />
                <Button onClick={abrirFolha} className="bg-green-700 hover:bg-green-800" data-testid="folha-abrir-btn">Conferir folha</Button>
              </div>
            ) : (
              <div className="space-y-2" data-testid="folha-conferencia">
                <p className="text-[11px] text-blue-800 bg-blue-50 rounded px-2 py-1">✏️ Valores editáveis — ajuste aumentos, encargos ou gratificação deste mês antes de confirmar.</p>
                {folha.map((it, i) => {
                  const jaLancado = lancados.includes(it.worker_id);
                  return (
                    <div key={it.worker_id} className={`border rounded-lg p-2.5 ${jaLancado ? "opacity-60 bg-gray-50" : ""}`} data-testid={`folha-item-${i}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold">{it.nome}</span>
                        {jaLancado ? <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full">✓ já lançada</span> : <span className="text-xs font-bold text-gray-700">{BRL(totalFolha(it))}</span>}
                      </div>
                      {!jaLancado && (
                        <div className="grid grid-cols-3 gap-1.5 mt-1.5">
                          <div><label className="text-[9px] text-gray-500">Salário</label><Input type="number" step="any" className="h-8 text-xs" value={it.salario} onChange={(e) => setFolhaItem(i, "salario", e.target.value)} data-testid={`folha-salario-${i}`} /></div>
                          <div><label className="text-[9px] text-gray-500">Encargos</label><Input type="number" step="any" className="h-8 text-xs" value={it.encargos} onChange={(e) => setFolhaItem(i, "encargos", e.target.value)} data-testid={`folha-encargos-${i}`} /></div>
                          <div><label className="text-[9px] text-gray-500">Gratificação</label><Input type="number" step="any" className="h-8 text-xs" value={it.gratificacao} onChange={(e) => setFolhaItem(i, "gratificacao", e.target.value)} data-testid={`folha-gratificacao-${i}`} /></div>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setFolha(null)} className="flex-1">Cancelar</Button>
                  <Button onClick={confirmarFolha} disabled={salvando} className="flex-1 bg-green-700 hover:bg-green-800" data-testid="folha-confirmar-btn">{salvando ? "Lançando..." : `💵 Lançar folha de ${folhaMonth.split("-").reverse().join("/")}`}</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
